import React, { useEffect, useState } from 'react';
import {
  MultipleCheckboxSubmittedAnswerType,
  MultipleChoiceData,
  MultipleChoiceSubmittedAnswerType,
  TrueFalseData,
} from '../../types/quiz';
import { useQuizContext } from '../../context/QuizContext';
import { Flex, Box } from '@osmosis/react-ui/components/Box';
import AnswerChoiceOption from './types/answer-choice-option/AnswerChoiceOption';
import AnswerCheckboxOption from './types/answer-checkbox-option/AnswerCheckboxOption';
import QuizSubmitButtons from './QuizSubmitButtons';
import {
  EliminationToolToggle,
  CorrectBanner,
  SkipQuizButton,
} from './types/utils';
import { P } from '@osmosis/react-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faKeyboard } from '@fortawesome/pro-regular-svg-icons/faKeyboard';
import { useIntl } from 'react-intl';
import { QuizType } from '@osmosis/shared/types/quiz';
import useModal from '../../hooks/useModal';
import ShortcutSnackbar from './ShortcutSnackbar';

/**
 * AnswerChoice.
 * @throws No quiz.
 */
const AnswerChoice = () => {
  const {
    currentItem,
    currentResponseItem,
    isAnswered,
    submitAnswer,
    eliminationItems,
    isEliminated,
    setShowElimination,
  } = useQuizContext();
  const intl = useIntl();

  const spacebarSnackbar = useModal();

  // Used for single-checked questions (MCQ, TF, etc)
  const [checkedOption, setCheckedOption] = useState<number>(-1);

  // Used for checkbox-type questions
  const [checkboxAnswers, setCheckboxAnswers] = useState<number[]>([]);

  const isCheckboxType = [
    QuizType.MultipleCheckbox,
    QuizType.ExtendedMultipleCheckbox,
  ].includes(currentItem?.subtype || -1);

  const isCheckedOptionType = [
    QuizType.MultipleChoice,
    QuizType.TrueFalse,
    QuizType.Exhibit,
    QuizType.Graphic,
  ].includes(currentItem?.subtype || -1);

  const checkedOptionResponse = currentResponseItem?.response
    ?.answer as MultipleChoiceSubmittedAnswerType;

  const currentCheckedOption =
    checkedOptionResponse > -1 ? checkedOptionResponse : checkedOption;

  const checkboxAnswersResponse = currentResponseItem?.response
    ?.answer as MultipleCheckboxSubmittedAnswerType;

  const currentCheckboxAnswers =
    checkboxAnswersResponse?.length > 0
      ? checkboxAnswersResponse
      : checkboxAnswers;

  // Reset various states when currentItem changes
  useEffect(() => {
    setCheckedOption(-1);
    setShowElimination(false);
  }, [currentItem?.nodeID]);

  // Show snackbar when answered
  useEffect(() => {
    if (isAnswered) {
      spacebarSnackbar.show();
    }
  }, [isAnswered]);

  // Protect against empty current item
  if (!currentItem) return null;

  const currentData = currentItem.data as MultipleChoiceData;

  if (!('answers' in currentItem.data))
    throw new Error('No answers in this Quiz Type');

  const correctAnswersLength = () => {
    if (!('answers' in currentItem.data)) return;
    const correctAnswers = currentData.answers.filter(
      (answer) => answer.isCorrect,
    );

    return correctAnswers.length;
  };
  const isCorrectAnswer = () => {
    if (!('answers' in currentItem.data)) return;
    let correctAnswer;
    switch (currentItem.subtype) {
      case QuizType.Graphic:
      case QuizType.MultipleChoice:
      case QuizType.Exhibit:
      case QuizType.TrueFalse:
        return (currentItem.data as TrueFalseData).answers[currentCheckedOption]
          ?.isCorrect;
      case QuizType.MultipleCheckbox:
      case QuizType.ExtendedMultipleCheckbox:
        correctAnswer = false;
        for (const checkboxAnswer of checkboxAnswers) {
          if (
            (currentItem.data as MultipleChoiceData).answers[checkboxAnswer]
              .isCorrect &&
            correctAnswersLength() === checkboxAnswers.length
          ) {
            correctAnswer = true;
          } else {
            return false;
          }
        }

        return correctAnswer;
      default:
        return false;
    }
  };
  const iconElement = <Icon key="icon" icon={faKeyboard} />;
  const message = intl.formatMessage(
    {
      id: 'press_Spacebar_To_Continue',
      defaultMessage: 'Press spacebar {icon} to continue ',
    },
    { icon: iconElement },
  );

  return (
    <>
      <ShortcutSnackbar message={message} snackbar={spacebarSnackbar} />
      <EliminationToolToggle />
      <Box>
        <Flex flexDirection="column" mt="15px">
          <Box
            as="fieldset"
            role={isCheckedOptionType ? 'radiogroup' : undefined}
            style={{ padding: 0, margin: 0 }}
          >
            <legend style={{ textAlign: 'left', paddingBottom: '20px' }}>
              {intl.formatMessage({
                id: 'choose_an_option',
                defaultMessage: 'Choose an option:',
              })}
            </legend>
            {currentData.answers.map(({ text, isCorrect, explanation }, i) => {
              if (isEliminated && eliminationItems.includes(i)) {
                return (
                  <P
                    as="li"
                    key={i}
                    style={{ visibility: 'hidden' }}
                    data-testid="hidden-answer"
                  ></P>
                );
              }
              if (isCheckedOptionType) {
                return (
                  <AnswerChoiceOption
                    key={`${currentItem.nodeID}-${text}`}
                    text={text}
                    isCorrect={isCorrect}
                    explanation={explanation}
                    index={i}
                    setCheckedOption={setCheckedOption}
                    checkedOption={currentCheckedOption}
                  />
                );
              }
              if (isCheckboxType) {
                return (
                  <AnswerCheckboxOption
                    key={`${currentItem.nodeID}-${text}`}
                    index={i}
                    text={text}
                    isCorrect={isCorrect}
                    explanation={explanation}
                    checked={currentCheckboxAnswers.includes(i)}
                    setCheckboxAnswers={setCheckboxAnswers}
                  />
                );
              }
            })}
          </Box>
        </Flex>
        {isAnswered && <CorrectBanner isCorrectAnswer={!!isCorrectAnswer()} />}
        {isCheckboxType && checkboxAnswers.length > 0 && !isAnswered && (
          <QuizSubmitButtons
            submit={(c) =>
              submitAnswer(c, !!isCorrectAnswer(), checkboxAnswers)
            }
          />
        )}

        <Flex justifyContent="flex-end">
          <SkipQuizButton />
        </Flex>
      </Box>
    </>
  );
};

export default AnswerChoice;
